import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getEventDreamTeam,
  getOverallDreamTeam,
} from "core-integration/src/store/dream-teams/reducers";
import {
  fetchEventDreamTeam,
  fetchOverallDreamTeam,
} from "core-integration/src/store/dream-teams/thunks";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import {
  getCurrentEvent,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ButtonLink from "../../ButtonLink";
import DeadlineCountdownBox from "../../DeadlineCountdownBox";
import Fixtures from "../../Fixtures";
import { ControlArrowRight } from "../../icons/Arrows";
import ManageYourLeagues from "../../leagues/admin/ManageYourLeagues";
import DreamTeamTable from "../../team/DreamTeamTable";
import ElementExplainDialog from "../../team/ElementExplainDialog";
import useEntryEventPitch from "../../team/hooks/useEntryEventPitch";
import PitchFormation from "../../team/PitchFormation";
import EventInfo from "../EventInfo";
import PlayerAvailabilityTable from "../PlayerAvailabilityTable";
import { PointsScoreboard } from "../PointsScoreboard";
import TransfersPanel from "../TransfersPanel";
import {
  ButtonLinkWrapper,
  CountdownGridContainer,
  DreamTeamGridContainer,
  DreamTeamHeading,
  EventInfoGridContainer,
  FixturesGridContainer,
  GridWrapper,
  PitchGridContainer,
  PitchHeading,
  PitchWrapper,
  PointsScoreboardGridContainer,
  TableWrap,
  TextWrap,
  TransfersGridContainer,
  TransfersWrap,
} from "./styles";

const Status = () => {
  const now = useSelector(getCurrentEvent);
  const player = useSelector((state: RootState) => getPlayerData(state));
  const entryId = player?.entry ?? 0;
  const entry = useSelector((state: RootState) => getEntry(state, entryId));
  const nextEvent = useSelector(getNextEvent);
  const [elementForMenu, setElementForMenu] = useState<number>(0);
  const reduxDispatch = useDispatch<ThunkDispatch>();
  const data = useSelector((state: RootState) =>
    now ? getEventDreamTeam(state, now.id) : getOverallDreamTeam(state),
  );

  const {
    entryData,
    formation,
    elementsDataById,
    pickForMenu,
    handleShowMenuForPickElement,
    handleHideMenuForPick,
    renderDreamTeam,
    renderPickValue,
  } = useEntryEventPitch(player?.entry, now?.id);

  const handleShowMenuForElement = (element: number) => {
    setElementForMenu(element);
  };

  const handleHideMenuForElement = () => {
    setElementForMenu(0);
  };

  const { t } = useTranslation();

  useEffect(() => {
    if (now) {
      reduxDispatch(fetchEventDreamTeam(now.id));
    } else {
      reduxDispatch(fetchOverallDreamTeam());
    }
  }, [now, reduxDispatch]);

  useEffect(() => {
    if (player?.entry) {
      reduxDispatch(fetchEntrySummary(player.entry));
    }
  }, [player?.entry, reduxDispatch]);

  if (!now) {
    return null;
  }

  const canRenderPoints = Boolean(
    entryData && entryData.picks && entryData.picks.length > 0 && formation,
  );

  const fixturesEventId = now.finished ? nextEvent?.id : now.id;
  const canShowCountdown = Boolean(now.finished && nextEvent);

  return (
    <>
      <GridWrapper>
        <PointsScoreboardGridContainer>
          <PointsScoreboard />
        </PointsScoreboardGridContainer>
        {canShowCountdown && (
          <CountdownGridContainer>
            <DeadlineCountdownBox />
          </CountdownGridContainer>
        )}
        {canRenderPoints ? (
          <PitchGridContainer>
            <PitchWrapper>
              <PitchHeading>{t("status.yourTeam", "Your Team")}</PitchHeading>
              <PitchFormation
                chipName={entryData!.active_chip}
                formation={formation!}
                picks={entryData!.picks}
                renderDreamTeam={renderDreamTeam}
                renderElementMenu={handleShowMenuForPickElement}
                renderPickValue={renderPickValue}
                eventId={now.id}
              />
            </PitchWrapper>
            <ButtonLinkWrapper>
              <ButtonLink
                to={
                  now.finished
                    ? `/entry/${entry.id}/event/${now.id}`
                    : "/my-team"
                }
                variant="quaternary"
              >
                {now.finished
                  ? t("status.viewPoints", "View points")
                  : t("navigation.myTeam", "My Team")}
                <ControlArrowRight />
              </ButtonLink>
            </ButtonLinkWrapper>
          </PitchGridContainer>
        ) : (
          <PitchGridContainer>
            <TextWrap>
              <p>
                {t(
                  "status.teamUpdating",
                  "Your team is updating for this round.",
                )}
              </p>
            </TextWrap>
          </PitchGridContainer>
        )}
        <FixturesGridContainer $canShowCountdown={canShowCountdown}>
          <Fixtures key={fixturesEventId} eventId={fixturesEventId} hidePager />
          <ManageYourLeagues entryId={entryId} />
          <PlayerAvailabilityTable playerCount={10} />
        </FixturesGridContainer>
        {/* EventInfo/Transfers/DreamTeam have no explicit grid-row, so their
         * stacking order here determines their auto-placed row. */}
        <EventInfoGridContainer>
          <EventInfo />
        </EventInfoGridContainer>
        {now.finished && (
          <TransfersGridContainer>
            <TransfersWrap>
              <TransfersPanel />
              <TransfersPanel isOut />
            </TransfersWrap>
          </TransfersGridContainer>
        )}
        {data && (
          <DreamTeamGridContainer>
            <TableWrap>
              <DreamTeamHeading>
                {now.name} {t("dreamTeam.dt", "Dream Team")}
              </DreamTeamHeading>
              <ButtonLinkWrapper>
                <ButtonLink to="/dream-team" variant="quaternary">
                  {t("dreamTeam.dt", "Dream Team")}
                  <ControlArrowRight />
                </ButtonLink>
              </ButtonLinkWrapper>
              <DreamTeamTable
                elements={data.team.map((e) => e.element)}
                renderElementMenu={handleShowMenuForElement}
                dataById={elementsDataById!}
              />
            </TableWrap>
          </DreamTeamGridContainer>
        )}

        {pickForMenu && (
          <ElementExplainDialog
            elementId={pickForMenu.element}
            eventId={now.id}
            closeDialog={handleHideMenuForPick}
          />
        )}
        {elementForMenu ? (
          <ElementExplainDialog
            elementId={elementForMenu}
            eventId={now.id}
            closeDialog={handleHideMenuForElement}
          />
        ) : null}
      </GridWrapper>
    </>
  );
};

export default Status;
